// DROPBOX: the web app's file system. The library IS a Dropbox folder
// (default /CTH-DB/Mindmaps). Shared with CTH Diagrams (web/dropbox.js there);
// only the storage keys and the default folder differ.
//
// AUTH IS PKCE, WITH NO SECRET ANYWHERE. A Dropbox app key for a public client
// is an identifier, not a credential, and PKCE needs no app secret. So this
// app has no server, no Worker route and no repo secret. The refresh token
// lives in this browser's localStorage only; Disconnect revokes it at Dropbox.

const LS = {
  key: 'cthmm.dbx.appkey.v1',
  tok: 'cthmm.dbx.token.v1',
  root: 'cthmm.dbx.root.v1',
};

export const DEFAULT_ROOT = '/CTH-DB/Mindmaps';

const API = 'https://api.dropboxapi.com/2';
const CONTENT = 'https://content.dropboxapi.com/2';

let mem = null; // { access_token, refresh_token, expires }

// Inside an embed (Notion) the browser may give this site partitioned storage,
// or none at all in a strict sandbox: every read and write falls back to memory,
// so the app still works for the session instead of breaking.
const memStore = new Map();
const lsGet = (k) => { try { const v = localStorage.getItem(k); if (v != null) return v; } catch (_) { /* no storage */ } return memStore.has(k) ? memStore.get(k) : null; };
const lsSet = (k, v) => { if (v == null) memStore.delete(k); else memStore.set(k, v); try { if (v == null) localStorage.removeItem(k); else localStorage.setItem(k, v); } catch (_) { /* memory only */ } };
const ssGet = (k) => { try { const v = sessionStorage.getItem(k); if (v != null) return v; } catch (_) { /* no storage */ } return memStore.has(`ss:${k}`) ? memStore.get(`ss:${k}`) : null; };
const ssSet = (k, v) => { if (v == null) memStore.delete(`ss:${k}`); else memStore.set(`ss:${k}`, v); try { if (v == null) sessionStorage.removeItem(k); else sessionStorage.setItem(k, v); } catch (_) { /* memory only */ } };

/** Running inside another page (a Notion embed). */
export const embedded = (() => { try { return window.self !== window.top; } catch (_) { return true; } })();
/** This browser keeps what the app saves (false in a strict sandbox: sign-in lasts the session only). */
export const storageOk = (() => { try { localStorage.setItem('__cth', '1'); localStorage.removeItem('__cth'); return true; } catch (_) { return false; } })();

// ---- configuration ---------------------------------------------------------

// Tony's Dropbox app ("CTH Studio", Scoped, Full Dropbox, PKCE allowed). An
// app key identifies a public client; it is not a secret. A key saved in
// Settings overrides it.
export const DEFAULT_APP_KEY = 'elbow181lbn7aq7';
export function appKey() { return lsGet(LS.key) || DEFAULT_APP_KEY; }
export function setAppKey(k) { lsSet(LS.key, String(k || '').trim() || null); }
export function libraryRoot() { return lsGet(LS.root) || DEFAULT_ROOT; }
export function setLibraryRoot(p) { lsSet(LS.root, normPath(p) || DEFAULT_ROOT); }

export function normPath(p) {
  let s = String(p || '').trim().replace(/\\/g, '/');
  if (!s || s === '/') return '';
  if (!s.startsWith('/')) s = `/${s}`;
  return s.replace(/\/+/g, '/').replace(/\/+$/, '');
}

function loadTok() {
  if (mem) return mem;
  try { mem = JSON.parse(lsGet(LS.tok) || 'null'); } catch (_) { mem = null; }
  return mem;
}
function saveTok(t) { mem = t; lsSet(LS.tok, t ? JSON.stringify(t) : null); }

export function connected() { const t = loadTok(); return !!(t && t.refresh_token); }

// ---- PKCE ------------------------------------------------------------------

function b64url(buf) {
  return btoa(String.fromCharCode(...new Uint8Array(buf))).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/, '');
}
async function sha256(s) { return crypto.subtle.digest('SHA-256', new TextEncoder().encode(s)); }
function randomVerifier() { const a = new Uint8Array(64); crypto.getRandomValues(a); return b64url(a).slice(0, 96); }

// Dropbox matches the redirect EXACTLY, so it is the bare origin + path. The
// hash (the folder or mindmap open) is kept in sessionStorage across the trip.
export function redirectUri() { return `${location.origin}${location.pathname}`; }

async function authUrl(verifier, state) {
  const key = appKey();
  if (!key) throw new Error('Add your Dropbox app key first.');
  const u = new URL('https://www.dropbox.com/oauth2/authorize');
  u.searchParams.set('client_id', key);
  u.searchParams.set('response_type', 'code');
  u.searchParams.set('redirect_uri', redirectUri());
  u.searchParams.set('code_challenge', b64url(await sha256(verifier)));
  u.searchParams.set('code_challenge_method', 'S256');
  u.searchParams.set('token_access_type', 'offline'); // gives the refresh token
  u.searchParams.set('state', state);
  return u.toString();
}

export async function beginAuth() {
  if (embedded) return beginEmbedAuth();
  const verifier = randomVerifier();
  const url = await authUrl(verifier, 'app');
  ssSet('cthmm.dbx.pkce', verifier);
  ssSet('cthmm.dbx.back', location.hash || '');
  location.href = url;
  return { redirected: true };
}

// ---- sign-in inside an embed --------------------------------------------------
// Dropbox's sign-in page refuses to load inside a frame, so an embed opens it
// in its own window. The redirect lands on this site in that window, which
// hands the one-time code back to the embed (postMessage to its opener) and
// closes. Where the window has no way back (Notion's desktop and phone apps
// open links in the normal browser), that page shows the code instead and the
// embed takes it pasted. The code is useless without the embed's own verifier.
let pendingEmbed = null;
export async function beginEmbedAuth() {
  const verifier = randomVerifier();
  const nonce = new Uint8Array(12); crypto.getRandomValues(nonce);
  const state = `embed.${b64url(nonce)}`;
  const url = await authUrl(verifier, state);
  pendingEmbed = { verifier, state };
  ssSet('cthmm.dbx.embed', JSON.stringify(pendingEmbed));
  let win = null;
  try { win = window.open(url, 'cth-dropbox-signin', 'popup,width=560,height=760'); } catch (_) { win = null; }
  return { opened: !!win, url };
}
export function embedAuthPending() { return !!(pendingEmbed || ssGet('cthmm.dbx.embed')); }
/** Finish an embed sign-in with the code from the Dropbox window (sent or pasted). */
export async function finishEmbedAuth(code, state = null) {
  const p = pendingEmbed || JSON.parse(ssGet('cthmm.dbx.embed') || 'null');
  if (!p) throw new Error('Click Connect Dropbox first, then paste the code.');
  if (state && state !== p.state) return false; // another embed's sign-in: not ours
  const clean = String(code || '').trim();
  if (!clean) throw new Error('Paste the code from the Dropbox window.');
  await exchangeCode(clean, p.verifier);
  pendingEmbed = null; ssSet('cthmm.dbx.embed', null);
  return true;
}
/** Listen for the code the Dropbox window posts back. */
export function onEmbedCode(fn) {
  window.addEventListener('message', (e) => {
    if (e.origin !== location.origin || !e.data || e.data.type !== 'cth-dropbox-code') return;
    fn(e.data.code, e.data.state);
  });
}
/**
 * This page is the Dropbox window of an embed sign-in (state "embed.*"):
 * hand the code to the embed that opened it. Returns { code, sent, error } or
 * null when this is not such a page. Never exchanges the code itself.
 */
export function relayEmbedCode() {
  const q = new URLSearchParams(location.search);
  const state = q.get('state') || '';
  if (!state.startsWith('embed.')) return null;
  const code = q.get('code') || '';
  const error = q.get('error_description') || q.get('error') || '';
  history.replaceState(null, '', location.pathname);
  let sent = false;
  try { if (code && window.opener && window.opener !== window) { window.opener.postMessage({ type: 'cth-dropbox-code', code, state }, location.origin); sent = true; } } catch (_) { sent = false; }
  return { code, state, sent, error };
}

async function exchangeCode(code, verifier) {
  const r = await fetch('https://api.dropboxapi.com/oauth2/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ code, grant_type: 'authorization_code', client_id: appKey(), code_verifier: verifier, redirect_uri: redirectUri() }),
  });
  if (!r.ok) throw new Error(r.status === 400 ? 'Dropbox did not accept that code (it works once, for a few minutes). Click Connect Dropbox again.' : `Dropbox refused the sign-in (${r.status}).`);
  const j = await r.json();
  saveTok({ access_token: j.access_token, refresh_token: j.refresh_token, expires: Date.now() + (j.expires_in || 14400) * 1000 - 60000 });
}

/** Call once at boot. True if it consumed an auth code. */
export async function finishAuth() {
  const q = new URLSearchParams(location.search);
  const code = q.get('code');
  const err = q.get('error_description') || q.get('error');
  if (!code && !err) return false;
  if ((q.get('state') || '').startsWith('embed.')) return false; // an embed's sign-in window: relayEmbedCode handles it
  const verifier = ssGet('cthmm.dbx.pkce');
  const back = ssGet('cthmm.dbx.back') || '';
  ssSet('cthmm.dbx.pkce', null);
  ssSet('cthmm.dbx.back', null);
  // Clean the URL before anything can fail, so a bad code is not replayed.
  history.replaceState(null, '', `${location.pathname}${back}`);
  if (err) throw new Error(`Dropbox sign-in was cancelled (${err}).`);
  if (!verifier) throw new Error('That sign-in did not start here. Try Connect again.');
  await exchangeCode(code, verifier);
  return true;
}

async function token() {
  const t = loadTok();
  if (!t || !t.refresh_token) throw new Error('NOT_CONNECTED');
  if (t.access_token && Date.now() < t.expires) return t.access_token;
  const r = await fetch('https://api.dropboxapi.com/oauth2/token', {
    method: 'POST',
    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
    body: new URLSearchParams({ grant_type: 'refresh_token', refresh_token: t.refresh_token, client_id: appKey() }),
  });
  if (!r.ok) { if (r.status === 400 || r.status === 401) saveTok(null); throw new Error('NOT_CONNECTED'); }
  const j = await r.json();
  const next = { ...t, access_token: j.access_token, expires: Date.now() + (j.expires_in || 14400) * 1000 - 60000 };
  saveTok(next);
  return next.access_token;
}

export async function disconnect() {
  try { await rpc('/auth/token/revoke', null); } catch (_) { /* revoking a dead token is fine */ }
  saveTok(null);
}

// ---- calls -----------------------------------------------------------------

export class DbxError extends Error {
  constructor(msg, status, body) { super(msg); this.status = status; this.body = body; }
}

// Dropbox-API-Arg must be header-safe ASCII: escape everything above 0x7e.
const NON_ASCII = /[\u007f-￿]/g;
const apiArg = (o) => JSON.stringify(o).replace(NON_ASCII, (c) => `\\u${c.charCodeAt(0).toString(16).padStart(4, '0')}`);

function dbxMessage(status, text) {
  if (/not_found/.test(text)) return 'That folder or file is not in Dropbox any more.';
  if (/conflict/.test(text)) return 'Something with that name is already there.';
  if (/insufficient_space/.test(text)) return 'Your Dropbox is full.';
  if (/too_many_requests|rate_limit/.test(text)) return 'Dropbox is busy. Wait a moment and try again.';
  if (/missing_scope/.test(text)) return 'Your Dropbox app is missing a permission (it needs files.metadata.read, files.content.read and files.content.write).';
  if (status === 409) return 'Dropbox would not accept that path.';
  return `Dropbox error ${status}.`;
}

async function call(url, { arg, body, content = false, retry = 2 } = {}) {
  const t = await token();
  const headers = { Authorization: `Bearer ${t}` };
  let payload = null;
  if (content) {
    headers['Dropbox-API-Arg'] = apiArg(arg);
    if (body) { headers['Content-Type'] = 'application/octet-stream'; payload = body; }
  } else if (arg !== null) {
    headers['Content-Type'] = 'application/json';
    payload = JSON.stringify(arg);
  }
  const r = await fetch(url, { method: 'POST', headers, body: payload });
  if (r.status === 429 && retry > 0) {
    const wait = Number(r.headers.get('Retry-After')) || 1;
    await new Promise((res) => setTimeout(res, wait * 1000));
    return call(url, { arg, body, content, retry: retry - 1 });
  }
  if (!r.ok) {
    const text = await r.text().catch(() => '');
    if (r.status === 401) { saveTok(null); throw new Error('NOT_CONNECTED'); }
    throw new DbxError(dbxMessage(r.status, text), r.status, text);
  }
  return r;
}
const rpc = async (path, arg) => { const r = await call(API + path, { arg }); return r.status === 204 ? null : r.json().catch(() => null); };

/** A request that never reached Dropbox (offline, wifi dropped, DNS): worth retrying later. */
export const isNetworkError = (e) => e instanceof TypeError || /Failed to fetch|NetworkError|Load failed|network/i.test(String(e?.message || ''));

export async function account() {
  const j = await rpc('/users/get_current_account', null);
  return { name: j?.name?.display_name || '', email: j?.email || '' };
}

const entry = (e) => ({
  id: e.id || '', // stable across renames and moves: an open file follows its own id
  dir: e['.tag'] === 'folder',
  name: e.name,
  path: e.path_display,
  lower: e.path_lower,
  size: e.size || 0,
  rev: e.rev || '',
  modified: e.server_modified ? Date.parse(e.server_modified) : 0,
  created: e.client_modified ? Date.parse(e.client_modified) : 0,
});

/** Everything under the root in one recursive listing (pagination followed to the end). */
export async function listAll(root = libraryRoot()) {
  const p = normPath(root);
  let res;
  try {
    res = await rpc('/files/list_folder', { path: p, recursive: true, limit: 2000, include_non_downloadable_files: false });
  } catch (e) {
    if (e instanceof DbxError && /not_found/.test(e.body || '')) return null; // the folder does not exist yet
    throw e;
  }
  const out = [...(res.entries || [])];
  while (res.has_more) {
    res = await rpc('/files/list_folder/continue', { cursor: res.cursor });
    out.push(...(res.entries || []));
  }
  return out.filter((e) => e['.tag'] !== 'deleted' && e.path_lower !== p.toLowerCase()).map(entry);
}

export async function meta(path) { return entry(await rpc('/files/get_metadata', { path: normPath(path) })); }

export async function download(path) {
  const r = await call(`${CONTENT}/files/download`, { arg: { path: normPath(path) }, content: true });
  const m = JSON.parse(r.headers.get('Dropbox-API-Result') || '{}');
  return { bytes: new Uint8Array(await r.arrayBuffer()), meta: m && m.name ? entry({ ...m, '.tag': 'file' }) : null };
}

/** A small PNG preview for a library card. Falls back to the file itself. */
export async function thumbnail(path) {
  const r = await call(`${CONTENT}/files/get_thumbnail_v2`, {
    arg: { resource: { '.tag': 'path', path: normPath(path) }, format: 'png', size: 'w640h480', mode: 'bestfit' },
    content: true,
  });
  return r.blob();
}

/**
 * Write a file. `rev` given: only if the file is still that revision (Dropbox
 * refuses with a conflict otherwise, so an edit made elsewhere meanwhile is
 * never silently overwritten). No rev: a new file, never over an existing one.
 */
export async function upload(path, bytes, { rev = null, overwrite = false } = {}) {
  const mode = rev ? { '.tag': 'update', update: rev } : overwrite ? { '.tag': 'overwrite' } : { '.tag': 'add' };
  const r = await call(`${CONTENT}/files/upload`, { arg: { path: normPath(path), mode, autorename: false, mute: true, strict_conflict: !!rev }, body: bytes, content: true });
  return entry({ ...(await r.json()), '.tag': 'file' });
}

export async function createFolder(path) {
  const j = await rpc('/files/create_folder_v2', { path: normPath(path), autorename: false });
  return entry({ ...j.metadata, '.tag': 'folder' });
}
export async function ensureFolder(path) {
  try { await createFolder(path); } catch (e) { if (!(e instanceof DbxError && /conflict/.test(e.body || ''))) throw e; }
}

export async function move(from, to) {
  const j = await rpc('/files/move_v2', { from_path: normPath(from), to_path: normPath(to), autorename: false, allow_ownership_transfer: false });
  return entry(j.metadata);
}
export async function copy(from, to) {
  const j = await rpc('/files/copy_v2', { from_path: normPath(from), to_path: normPath(to), autorename: false });
  return entry(j.metadata);
}
/** Dropbox keeps deleted files (restorable from dropbox.com for 30 days or more). */
export async function remove(path) { return rpc('/files/delete_v2', { path: normPath(path) }); }

/** Long-poll for changes under the root. Resolves true when something changed. */
export async function cursorFor(root = libraryRoot()) {
  const j = await rpc('/files/list_folder/get_latest_cursor', { path: normPath(root), recursive: true });
  return j.cursor;
}
export async function longpoll(cursor) {
  // No auth header on this endpoint by design.
  const r = await fetch('https://notify.dropboxapi.com/2/files/list_folder/longpoll', {
    method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ cursor, timeout: 120 }),
  });
  if (!r.ok) throw new Error(`longpoll ${r.status}`);
  const j = await r.json();
  return { changes: !!j.changes, backoff: j.backoff || 0 };
}

// A 4-hour direct URL to a file (cached for 3 hours), for pictures Dropbox
// will not thumbnail (over 20 MB).
const links = new Map();
export async function tempLink(path, { fresh = false } = {}) {
  const p = normPath(path).toLowerCase();
  const hit = links.get(p);
  if (!fresh && hit && Date.now() < hit.until) return hit.url;
  const j = await rpc('/files/get_temporary_link', { path: normPath(path) });
  links.set(p, { url: j.link, until: Date.now() + 3 * 3600e3 });
  return j.link;
}
